import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cmsCreateItem } from "api";

/* Keep overlays above transformed CMS rows, or inside Studio's native dialog
   when the CMS is embedded there. Also lock page scroll while mounted. */
export function ModalPortal({ children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  // A native Menu Studio dialog lives in the browser top layer. Keep CMS
  // overlays inside it so item and section forms remain interactive there.
  return createPortal(children, document.querySelector("dialog.menu-studio[open]") || document.body);
}

/* Two-step delete: first click arms the button and shows a short inline
   confirmation to its right; a second click actually deletes. Clicking
   anywhere else (blur) disarms it. */
export function DeleteButton({ label, onDelete }) {
  const [armed, setArmed] = useState(false);
  return (
    <span className="cms-delete-wrap">
      <button
        type="button"
        className={`cms-delete-item-btn cms-modal-delete${
          armed ? " cms-delete-armed" : ""
        }`}
        onClick={() => (armed ? onDelete() : setArmed(true))}
        onBlur={() => setArmed(false)}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
          <path d="M10 11v6M14 11v6" />
          <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
        </svg>
        {label}
      </button>
      {armed && (
        <span className="cms-delete-confirm">Click again to delete</span>
      )}
    </span>
  );
}

const MODIFIER_DEFS = [
  { key: "sizes", label: "Sizes", singular: "Size" },
  { key: "options", label: "Options", singular: "Option" },
  { key: "sauces", label: "Sauces", singular: "Sauce" },
  { key: "pastas", label: "Pastas", singular: "Pasta" },
  { key: "platters", label: "Platters", singular: "Platter" },
];

function ModifierSection({ label, singular, entries, onChange, existing }) {
  const [isOpen, setIsOpen] = useState(false);

  function addEntry() {
    onChange([...entries, { name: "", price: "" }]);
    if (!isOpen) setIsOpen(true);
  }

  function updateEntry(index, field, value) {
    onChange(entries.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  }

  function removeEntry(index) {
    onChange(entries.filter((_, i) => i !== index));
  }

  function addFromChip(name, price) {
    onChange([...entries, { name, price: String(price) }]);
    if (!isOpen) setIsOpen(true);
  }

  const chips = (existing || []).filter(
    ({ name }) => !entries.some((e) => e.name?.trim() === name.trim())
  );

  return (
    <div className={`cms-modifier-section${isOpen ? " cms-modifier-open" : ""}`}>
      <button
        type="button"
        className="cms-modifier-toggle"
        onClick={() => setIsOpen((p) => !p)}
      >
        <span>{label}</span>
        {entries.length > 0 && (
          <span className="cms-modifier-badge">{entries.length}</span>
        )}
        <svg
          className="cms-modifier-chevron"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="cms-modifier-body">
          {entries.map((entry, i) => (
            <div key={i} className="cms-modifier-row">
              <input
                className="cms-input cms-modifier-name-input"
                placeholder={`${singular} name`}
                value={entry.name}
                onChange={(e) => updateEntry(i, "name", e.target.value)}
              />
              <div className="cms-modifier-price-wrap">
                <span className="cms-price-symbol">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="cms-input cms-modifier-price-input"
                  placeholder="0.00"
                  value={entry.price}
                  onChange={(e) => updateEntry(i, "price", e.target.value)}
                />
              </div>
              <button
                type="button"
                className="cms-remove-btn"
                onClick={() => removeEntry(i)}
                title={`Remove ${singular.toLowerCase()}`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
          <button type="button" className="cms-add-entry-btn" onClick={addEntry}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add {singular}
          </button>
          {chips.length > 0 && (
            <div className="cms-existing-modifiers">
              <span className="cms-chips-label">Quick add:</span>
              {chips.map(({ name, price }) => (
                <button
                  key={name}
                  type="button"
                  className="cms-modifier-chip"
                  onClick={() => addFromChip(name, price)}
                >
                  {name}
                  {price > 0 && (
                    <span className="cms-chip-price">+${Number(price).toFixed(2)}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ItemEditForm({ item, sectionId, onSave, onCancel, onDelete, existingModifiers, navigation }) {
  const isCreate = !item;

  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [prices, setPrices] = useState(
    item?.price?.length > 0 ? item.price.map(String) : []
  );
  const [modifiers, setModifiers] = useState({
    sizes: item?.sizes ?? [],
    options: item?.options ?? [],
    sauces: item?.sauces ?? [],
    pastas: item?.pastas ?? [],
    platters: item?.platters ?? [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function setModifier(key, value) {
    setModifiers((prev) => ({ ...prev, [key]: value }));
  }

  function parseModifiers(arr) {
    return arr
      .filter((e) => e.name?.trim())
      .map((e) => ({
        ...(e._id ? { _id: e._id } : {}),
        name: e.name.trim(),
        price: parseFloat(e.price) || 0,
      }));
  }

  function buildPayload() {
    return {
      name: name.trim(),
      description: description.trim(),
      price: prices
        .map((p) => parseFloat(p))
        .filter((n) => !isNaN(n) && n >= 0),
      sizes: parseModifiers(modifiers.sizes),
      options: parseModifiers(modifiers.options),
      sauces: parseModifiers(modifiers.sauces),
      pastas: parseModifiers(modifiers.pastas),
      platters: parseModifiers(modifiers.platters),
    };
  }

  const initialPayload = useRef(JSON.stringify(buildPayload()));
  const busy = useRef(false);

  async function handleSave(direction = 0) {
    if (busy.current || !name.trim()) return;
    setError(null);
    const payload = buildPayload();
    if (direction && JSON.stringify(payload) === initialPayload.current) {
      navigation.onMove(direction);
      return;
    }
    busy.current = true;
    setSaving(true);
    try {
      if (isCreate) {
        const newItem = await cmsCreateItem({ ...payload, sectionId });
        await onSave(newItem);
      } else {
        await onSave(payload, direction);
      }
    } catch (err) {
      setError("Failed to save. Your edits are still here. Please try again.");
    } finally {
      busy.current = false;
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (busy.current) return;
    busy.current = true;
    setSaving(true);
    setError(null);
    try { await onDelete(); }
    catch (err) { setError("Failed to delete. Please try again."); }
    finally { busy.current = false; setSaving(false); }
  }

  function addPrice() {
    setPrices((prev) => [...prev, ""]);
  }

  function updatePrice(index, value) {
    setPrices((prev) => prev.map((p, i) => (i === index ? value : p)));
  }

  function removePrice(index) {
    setPrices((prev) => prev.filter((_, i) => i !== index));
  }

  return (
    <ModalPortal>
    <div
      className="cms-export-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy.current) onCancel();
      }}
    >
      <div
        className="cms-export-modal cms-item-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isCreate ? "New item" : `Edit ${item.name}`}
      >
        <header className="cms-export-head">
          <h3>{isCreate ? "New Item" : "Edit Item"}</h3>
          <p>
            {isCreate
              ? "Add a dish to this section."
              : item.name}
          </p>
          {navigation && (
            <div className="cms-item-navigation">
              <p aria-live="polite">{navigation.context}<br />{navigation.position} of {navigation.total} · Changes save when navigating</p>
              <div className="cms-export-foot-actions">
                <button type="button" className="cms-btn-cancel" disabled={saving || !name.trim() || !navigation.hasPrev} onClick={() => handleSave(-1)}>Prev</button>
                <button type="button" className="cms-btn-cancel" disabled={saving || !name.trim() || !navigation.hasNext} onClick={() => handleSave(1)}>Next</button>
              </div>
            </div>
          )}
        </header>

        <fieldset className="cms-modal-body cms-item-fields" disabled={saving}>
          <div className="cms-form-grid">
        {/* Name */}
        <div className="cms-form-group">
          <label className="cms-form-label">Name</label>
          <input
            className="cms-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Item name"
            autoFocus={isCreate}
          />
        </div>

        {/* Description */}
        <div className="cms-form-group cms-form-group-desc">
          <label className="cms-form-label">Description</label>
          <textarea
            className="cms-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description…"
            rows={2}
          />
        </div>

        {/* Base Prices */}
        <div className="cms-form-group">
          <label className="cms-form-label">Base Price(s)</label>
          <div className="cms-price-list">
            {prices.map((p, i) => (
              <div key={i} className="cms-price-row">
                <span className="cms-price-symbol">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="cms-input cms-price-input"
                  value={p}
                  onChange={(e) => updatePrice(i, e.target.value)}
                  placeholder="0.00"
                />
                <button
                  type="button"
                  className="cms-remove-btn"
                  onClick={() => removePrice(i)}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
            <button type="button" className="cms-add-entry-btn" onClick={addPrice}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Price
            </button>
          </div>
        </div>
      </div>

          {/* Modifier Accordion */}
          <div className="cms-modifiers-accordion">
            {MODIFIER_DEFS.map(({ key, label, singular }) => (
              <ModifierSection
                key={key}
                label={label}
                singular={singular}
                entries={modifiers[key]}
                onChange={(val) => setModifier(key, val)}
                existing={existingModifiers?.[key] || []}
              />
            ))}
          </div>

          {error && <p className="cms-form-error" role="alert">{error}</p>}
        </fieldset>

        <footer className="cms-export-foot">
          {onDelete ? (
            <DeleteButton label="Delete" onDelete={handleDelete} />
          ) : (
            <span />
          )}
          <div className="cms-export-foot-actions">
            <button type="button" className="cms-btn-cancel" disabled={saving} onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="cms-btn-save cms-export-go"
              onClick={() => handleSave()}
              disabled={saving || !name.trim()}
            >
              {saving ? "Saving…" : isCreate ? "Create Item" : "Save Changes"}
            </button>
          </div>
        </footer>
      </div>
    </div>
    </ModalPortal>
  );
}

export default ItemEditForm;
